<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PilotKnowledgeBasesAPI from 'dashboard/api/pilot/knowledgeBases';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FileIcon from 'dashboard/components-next/icon/FileIcon.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  knowledgeBase: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const DEFAULT_SEARCH_CONFIG = {
  search_mode: 'hybrid',
  rerank_enabled: true,
  vector_top_k: 10,
  full_text_top_k: 10,
  top_n: 5,
  faq_threshold: 0.6,
  doc_threshold: 0.5,
  faq_embedding_weight: 0.7,
  faq_full_text_weight: 0.3,
  doc_embedding_weight: 0.7,
  doc_full_text_weight: 0.3,
  per_kb_weight: 1.0,
};

const configValue = key => props.knowledgeBase?.[key] ?? DEFAULT_SEARCH_CONFIG[key];

const SEARCH_MAX_LENGTH = 500;

// 搜索参数
const searchQuery = ref('');
const searchScope = ref('all');
const rerankEnabled = ref(configValue('rerank_enabled'));
const vectorTopK = ref(configValue('vector_top_k'));
const fullTextTopK = ref(configValue('full_text_top_k'));
const topN = ref(configValue('top_n'));
const faqThreshold = ref(configValue('faq_threshold'));
const docThreshold = ref(configValue('doc_threshold'));
const searchMode = ref(configValue('search_mode'));
const faqEmbeddingWeight = ref(configValue('faq_embedding_weight'));
const faqFullTextWeight = computed(() => Number((1 - faqEmbeddingWeight.value).toFixed(2)));
const docEmbeddingWeight = ref(configValue('doc_embedding_weight'));
const docFullTextWeight = computed(() => Number((1 - docEmbeddingWeight.value).toFixed(2)));
const faqEmbeddingSlider = computed({
  get: () => faqEmbeddingWeight.value,
  set: value => {
    faqEmbeddingWeight.value = Number(Number(value).toFixed(2));
  },
});
const docEmbeddingSlider = computed({
  get: () => docEmbeddingWeight.value,
  set: value => {
    docEmbeddingWeight.value = Number(Number(value).toFixed(2));
  },
});
const perKbWeight = ref(configValue('per_kb_weight'));

const searchModes = computed(() => [
  {
    value: 'semantic',
    label: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_MODE_VECTOR'),
    desc: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_MODE_VECTOR_DESC'),
  },
  {
    value: 'full_text',
    label: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_MODE_KEYWORD'),
    desc: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_MODE_KEYWORD_DESC'),
  },
  {
    value: 'hybrid',
    label: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_MODE_HYBRID'),
    desc: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_MODE_HYBRID_DESC'),
  },
]);

const syncConfigFromKnowledgeBase = () => {
  rerankEnabled.value = configValue('rerank_enabled');
  vectorTopK.value = configValue('vector_top_k');
  fullTextTopK.value = configValue('full_text_top_k');
  topN.value = configValue('top_n');
  faqThreshold.value = configValue('faq_threshold');
  docThreshold.value = configValue('doc_threshold');
  searchMode.value = configValue('search_mode');
  faqEmbeddingWeight.value = configValue('faq_embedding_weight');
  docEmbeddingWeight.value = configValue('doc_embedding_weight');
  perKbWeight.value = configValue('per_kb_weight');
};

// 抽屉状态
const showConfigDrawer = ref(false);
watch(
  () => props.knowledgeBase,
  () => {
    syncConfigFromKnowledgeBase();
  },
  { immediate: true, deep: true }
);

// 结果
const isSearching = ref(false);
const searchResults = ref(null);
const historyQueries = ref([]);
const isHistoryLoading = ref(false);
const historyHasMore = ref(false);
const historyPage = ref(1);
const historyTotal = ref(0);
const errorMessage = ref('');

const faqResults = computed(() => searchResults.value?.faqs || []);
const documentResults = computed(() => searchResults.value?.documents || []);
const elapsedMs = computed(() => searchResults.value?.elapsed_ms || 0);
const faqTrace = computed(() => searchResults.value?.faq_trace || null);
const documentTrace = computed(() => searchResults.value?.document_trace || null);

const resultTab = ref('faq');
const resultTabIndex = computed({
  get: () => (resultTab.value === 'faq' ? 0 : 1),
  set: val => {
    resultTab.value = val === 0 ? 'faq' : 'document';
  },
});

const resultTabs = computed(() => [
  {
    label: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_TAB_FAQ'),
    count: faqResults.value.length,
  },
  {
    label: t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_TAB_DOCUMENT'),
    count: documentResults.value.length,
  },
]);

const activeTrace = computed(() =>
  resultTab.value === 'faq' ? faqTrace.value : documentTrace.value
);

const handleSearch = async () => {
  const query = searchQuery.value.trim();
  if (!query) return;

  isSearching.value = true;
  errorMessage.value = '';
  searchResults.value = null;

  try {
    const response = await PilotKnowledgeBasesAPI.recallTest(
      props.knowledgeBase.id,
      {
        query,
        search_scope: searchScope.value,
        rerank_enabled: rerankEnabled.value,
        vector_top_k: vectorTopK.value,
        full_text_top_k: fullTextTopK.value,
        top_n: topN.value,
        faq_threshold: faqThreshold.value,
        doc_threshold: docThreshold.value,
        search_mode: searchMode.value,
        faq_embedding_weight: faqEmbeddingWeight.value,
        faq_full_text_weight: faqFullTextWeight.value,
        doc_embedding_weight: docEmbeddingWeight.value,
        doc_full_text_weight: docFullTextWeight.value,
      }
    );

    searchResults.value = response.data;

    // 自动切换到有结果的 tab
    if (searchResults.value.faqs?.length > 0) resultTab.value = 'faq';
    else if (searchResults.value.documents?.length > 0)
      resultTab.value = 'document';

    // 将新记录插入历史列表顶部，不重新请求
    historyQueries.value = [
      {
        id: Date.now(),
        query,
        search_scope: searchScope.value,
        elapsed_ms: response.data.elapsed_ms,
        faq_count: response.data.faqs?.length || 0,
        document_count: response.data.documents?.length || 0,
        created_at: new Date().toISOString(),
      },
      ...historyQueries.value,
    ];
    historyTotal.value += 1;
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.ERROR_TITLE');
  } finally {
    isSearching.value = false;
  }
};

const handleHistoryClick = item => {
  searchQuery.value = item.query;
  handleSearch();
};

const formatTime = date => {
  if (!date) return '';
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const isSavingConfig = ref(false);
const confirmSaveDialogRef = ref(null);

const handleSaveConfig = () => {
  confirmSaveDialogRef.value?.open();
};

const handleSaveConfigConfirm = async () => {
  isSavingConfig.value = true;
  try {
    await PilotKnowledgeBasesAPI.update(props.knowledgeBase.id, {
      rerank_enabled: rerankEnabled.value,
      vector_top_k: vectorTopK.value,
      full_text_top_k: fullTextTopK.value,
      top_n: topN.value,
      faq_threshold: faqThreshold.value,
      doc_threshold: docThreshold.value,
      search_mode: searchMode.value,
      faq_embedding_weight: faqEmbeddingWeight.value,
      faq_full_text_weight: faqFullTextWeight.value,
      doc_embedding_weight: docEmbeddingWeight.value,
      doc_full_text_weight: docFullTextWeight.value,
      per_kb_weight: perKbWeight.value,
    });
    useAlert(t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SAVE_CONFIG_SUCCESS'));
    confirmSaveDialogRef.value?.close();
    showConfigDrawer.value = false;
  } catch (error) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SAVE_CONFIG_ERROR'));
  } finally {
    isSavingConfig.value = false;
  }
};

const loadHistories = async (append = false) => {
  isHistoryLoading.value = true;
  try {
    const page = append ? historyPage.value + 1 : 1;
    const response = await PilotKnowledgeBasesAPI.getRecallTestHistories(
      props.knowledgeBase.id,
      { page, per_page: 20 }
    );
    const newItems = response.data?.histories || [];
    if (append) {
      historyQueries.value = [...historyQueries.value, ...newItems];
      historyPage.value = page;
    } else {
      historyQueries.value = newItems;
      historyPage.value = 1;
    }
    historyHasMore.value = response.data?.has_more || false;
    historyTotal.value = response.data?.total_count || 0;
  } catch {
    // 静默失败
  } finally {
    isHistoryLoading.value = false;
  }
};

const handleClearHistory = async () => {
  if (!window.confirm(t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_CLEAR_CONFIRM'))) return;
  try {
    await PilotKnowledgeBasesAPI.clearRecallTestHistories(
      props.knowledgeBase.id
    );
    historyQueries.value = [];
  } catch {
    useAlert(t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_CLEAR_ERROR'));
  }
};

onMounted(() => {
  loadHistories();
});

const formatScore = score => {
  if (score === null || score === undefined) return '-';
  return score.toFixed(4);
};

const primaryScoreLabel = computed(() => {
  if (searchMode.value === 'full_text') {
    return t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_FULL_TEXT_SCORE');
  }
  if (searchMode.value === 'hybrid') {
    return t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_HYBRID_SCORE');
  }
  return t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_SEMANTIC_SCORE');
});

const scoreBasisLabel = basis => {
  switch (basis) {
    case 'normalized_semantic_score':
      return t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_SEMANTIC_SCORE');
    case 'normalized_full_text_score':
      return t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_FULL_TEXT_SCORE');
    case 'normalized_hybrid_score':
      return t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_HYBRID_SCORE');
    case 'rerank_score':
      return t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_RERANK_SCORE');
    default:
      return basis || '-';
  }
};

const processSummary = computed(() => {
  const trace = activeTrace.value;
  if (!trace) return [];
  const isHybrid = trace.search_mode === 'hybrid';

  return [
    { label: '粗召回', value: `语义 ${trace.vector_count ?? 0} / 全文 ${trace.full_text_count ?? 0}` },
    ...(isHybrid ? [{ label: '合并候选', value: trace.merge_count ?? 0 }] : []),
    ...(trace.rerank_enabled ? [{ label: 'Rerank处理', value: trace.rerank_count ?? 0 }] : []),
    { label: '阈值后', value: trace.post_score_filter_count ?? 0 },
    { label: '最终Top N', value: trace.final_count ?? 0 },
  ];
});

const currentTraceCandidate = computed(() => {
  const trace = activeTrace.value;
  const candidateId = expandedItem.value?.data?.id;
  if (!trace || !candidateId) return null;

  return (trace.pre_rerank_candidates || []).find(item => item.id === candidateId)
    || (trace.final_candidates || []).find(item => item.id === candidateId)
    || null;
});

const processStages = computed(() => {
  const trace = activeTrace.value;

  const item = currentTraceCandidate.value;
  if (!trace || !item) return [];
  const isHybrid = trace.search_mode === 'hybrid';

  const selectedIds = trace.selected_ids || [];
  const finalIds = trace.final_selected_ids || selectedIds;
  const finalRank = finalIds.indexOf(item.id);
  const hasSemantic = item.normalized_semantic_score !== null && item.normalized_semantic_score !== undefined;
  const hasFullText = item.normalized_full_text_score !== null && item.normalized_full_text_score !== undefined;
  const thresholdScore =
    trace.score_basis === 'rerank_score' ? item.rerank_score : item.final_score;
  const thresholdPassed =
    thresholdScore !== null &&
    thresholdScore !== undefined &&
    Number(thresholdScore) >= Number(trace.threshold ?? 0);

  return [
    {
      key: 'recall',
      title: '粗召回',
      meta: [
        hasSemantic ? `命中语义 ${formatScore(item.normalized_semantic_score ?? item.semantic_score)}` : null,
        hasFullText ? `命中全文 ${formatScore(item.normalized_full_text_score ?? item.full_text_score)}` : null,
      ].filter(Boolean).join(' / ') || '未命中',
    },
    ...(isHybrid ? [{
      key: 'merge',
      title: '合并候选',
      meta: `混合分 ${formatScore(item.normalized_hybrid_score ?? item.final_score)}`,
    }] : []),
    ...(trace.rerank_enabled ? [{
      key: 'rerank',
      title: 'Rerank',
      meta: item.rerank_score != null
        ? `进入重排，得分 ${formatScore(item.rerank_score)}`
        : '未进入重排',
    }] : []),
    {
      key: 'threshold',
      title: '阈值过滤',
      meta: `${scoreBasisLabel(trace.score_basis)} ${formatScore(thresholdScore)} / 阈值 ${formatScore(trace.threshold)}`,
      status: thresholdPassed ? '通过' : '淘汰',
    },
    {
      key: 'final',
      title: '最终结果',
      meta: finalRank >= 0 ? `进入最终结果，第 ${finalRank + 1} 名` : '未进入最终结果',
    },
  ];
});

const scoreRowsForItem = item => {
  if (!item) return [];

  return [
    { label: '语义分', value: item.semantic_score },
    { label: '全文分', value: item.full_text_score },
    ...(!rerankEnabled.value ? [{ label: '归一混合分', value: item.normalized_hybrid_score }] : []),
    { label: 'Rerank分', value: item.rerank_score },
  ].filter(row => row.value !== null && row.value !== undefined);
};

const recallSourceForItem = item => {
  if (!item) return null;

  const hasSemantic =
    item.normalized_semantic_score !== null &&
    item.normalized_semantic_score !== undefined;
  const hasFullText =
    item.normalized_full_text_score !== null &&
    item.normalized_full_text_score !== undefined;

  if (hasSemantic && hasFullText) {
    return { label: '双路命中', tone: 'dual' };
  }
  if (hasSemantic) {
    return { label: '语义命中', tone: 'semantic' };
  }
  if (hasFullText) {
    return { label: '全文命中', tone: 'full_text' };
  }

  return null;
};

const recallSourceClass = tone => {
  switch (tone) {
    case 'semantic':
      return 'bg-n-brand/10 text-n-brand';
    case 'full_text':
      return 'bg-n-warning/10 text-n-warning';
    case 'dual':
      return 'bg-n-success/10 text-n-success';
    default:
      return 'bg-n-slate-2 text-n-slate-11 dark:bg-n-slate-700 dark:text-n-slate-200';
  }
};

// 根据当前模式获取检索阶段主分数（不受 Rerank 影响）
const getPrimaryScore = item => {
  if (searchMode.value === 'full_text') return item.normalized_full_text_score ?? item.full_text_score;
  if (searchMode.value === 'hybrid') return item.normalized_hybrid_score;
  return item.normalized_semantic_score ?? item.semantic_score;
};

// 分数是否在 0~1 范围（用于进度条展示）
const isNormalizedScore = () => {
  return true;
};

const scoreColor = 'text-n-brand';

const scoreBadgeBar = 'bg-n-brand';
const scoreBadgeText = 'text-n-brand';

// 弹窗：查看完整内容
const expandedItem = ref(null);

// 帮助弹窗
const helpDialogRef = ref(null);
const helpDialogType = ref('recall');
const modeConfigText = {
  semantic: '基于问题语义召回候选，适合用户问法变化大、关键词不固定的查询。',
  full_text: '基于分词和关键词匹配召回候选，适合名称、术语、固定表达明确的查询。',
  hybrid: '同时走语义和全文召回，合并去重后统一排序，通常是默认推荐模式。',
};
const hybridMode = computed(() => (rerankEnabled.value ? 'rerank' : 'weighted'));

const recommendedScoreRange = computed(() => {
  if (rerankEnabled.value) {
    return {
      faq: '0.45 ~ 0.75',
      document: '0.35 ~ 0.65',
    };
  }

  switch (searchMode.value) {
    case 'semantic':
      return {
        faq: '0.45 ~ 0.65',
        document: '0.25 ~ 0.45',
      };
    case 'full_text':
      return {
        faq: '0.20 ~ 0.45',
        document: '0.15 ~ 0.35',
      };
    default:
      return {
        faq: '0.35 ~ 0.60',
        document: '0.25 ~ 0.45',
      };
  }
});

</script>

<template>
  <div class="flex gap-6 h-full min-h-0">
    <!-- 左侧：搜索 + 历史 -->
    <div class="w-1/2 flex-shrink-0 flex flex-col gap-5 min-h-0 overflow-hidden">
      <!-- 搜索输入区 -->
      <section class="bg-[#F2F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.QUERY_LABEL') }}
            </h3>
            <span
              v-tooltip="t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.QUERY_HELP')"
              class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
            />
          </div>
          <!-- 向量检索配置按钮 -->
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-12 bg-white dark:bg-n-slate-800 rounded-lg shadow-sm hover:shadow-md hover:bg-n-slate-50 dark:hover:bg-n-slate-700 transition-all"
            @click="showConfigDrawer = true"
          >
            <span class="i-lucide-settings-2 text-sm" />
            {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.CONFIG_BUTTON') }}
          </button>
        </div>
        <TextArea
          v-model="searchQuery"
          :max-length="SEARCH_MAX_LENGTH"
          :show-character-count="true"
          :auto-height="true"
          min-height="140px"
          max-height="300px"
          :placeholder="t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_PLACEHOLDER')"
          custom-text-area-wrapper-class="!bg-white dark:!bg-n-slate-800"
          @keydown.ctrl.enter="handleSearch"
          @keydown.meta.enter="handleSearch"
        />
        <div class="flex items-center justify-between mt-3">
          <div class="flex items-center gap-2">
            <span
              class="inline-flex items-center gap-1 px-2 py-1 text-xs text-n-brand bg-n-brand/10 rounded-md cursor-pointer hover:bg-n-brand/15 transition-colors"
              @click="helpDialogType = 'recall'; helpDialogRef?.open()"
            >
              <span class="i-lucide-help-circle text-xs" />
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.WHAT_IS_RECALL') }}
            </span>
            <span
              class="inline-flex items-center gap-1 px-2 py-1 text-xs text-n-brand bg-n-brand/10 rounded-md cursor-pointer hover:bg-n-brand/15 transition-colors"
              @click="helpDialogType = 'rerank'; helpDialogRef?.open()"
            >
              <span class="i-lucide-help-circle text-xs" />
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.WHAT_IS_RERANK') }}
            </span>
          </div>
          <Button
            :label="
              isSearching
                ? t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCHING')
                : t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_BUTTON')
            "
            icon="i-lucide-search"
            :is-loading="isSearching"
            @click="handleSearch"
          />
        </div>
      </section>

      <!-- 搜索范围（独立区块） -->
      <div class="flex flex-col gap-2">
        <span class="text-base font-medium text-n-slate-12 dark:text-n-slate-100">{{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SCOPE_LABEL') }}</span>
        <div class="flex items-center gap-4">
          <label
            v-for="scope in ['all', 'faq', 'document']"
            :key="scope"
            class="flex items-center gap-1.5 cursor-pointer"
          >
            <input
              v-model="searchScope"
              type="radio"
              :value="scope"
              class="w-3.5 h-3.5 text-n-brand"
            />
            <span class="text-sm text-n-slate-12 dark:text-n-slate-100">
              {{
                t(
                  `PILOT.KNOWLEDGE_BASES.RECALL_TEST.SCOPE_${scope === 'all' ? 'ALL' : scope === 'faq' ? 'FAQ' : 'DOCUMENT'}`
                )
              }}
            </span>
          </label>
        </div>
      </div>

      <!-- 历史查询 -->
      <div class="flex flex-col gap-2 flex-1 min-h-0">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-medium text-n-slate-12 dark:text-n-slate-100">
            {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_TITLE') }}
          </h3>
          <div class="flex items-center gap-2">
            <span
              v-if="historyTotal > 0"
              class="text-xs text-n-slate-10"
            >
              {{ historyTotal }}
            </span>
            <button
              v-if="historyQueries.length > 0"
              type="button"
              class="flex items-center gap-1 px-2 py-1 text-xs text-n-slate-10 hover:text-n-red-10 hover:bg-n-red-1 dark:hover:bg-n-red-2 rounded-md transition-colors"
              @click="handleClearHistory"
            >
              <span class="i-lucide-trash-2 text-xs" />
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_CLEAR') }}
            </button>
          </div>
        </div>

        <!-- 历史列表 -->
        <div v-if="historyQueries.length > 0" class="flex-1 overflow-y-auto">
          <!-- 表头 -->
          <div class="grid grid-cols-[1fr_64px_80px] gap-2 px-3 py-1.5 text-xs font-medium text-n-slate-10 border-b border-n-slate-3 dark:border-n-slate-700">
            <span>{{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_COL_QUERY') }}</span>
            <span class="text-right">{{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_COL_ELAPSED') }}</span>
            <span class="text-right">{{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_COL_TIME') }}</span>
          </div>
          <!-- 数据行 -->
          <div
            v-for="item in historyQueries"
            :key="item.id"
            class="grid grid-cols-[1fr_64px_80px] gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 dark:hover:bg-n-slate-700 rounded-lg cursor-pointer transition-colors"
            @click="handleHistoryClick(item)"
          >
            <span class="truncate">{{ item.query }}</span>
            <span class="text-right text-xs text-n-slate-10 tabular-nums">{{ item.elapsed_ms }}ms</span>
            <span class="text-right text-xs text-n-slate-10">{{ formatTime(item.created_at) }}</span>
          </div>
          <!-- 加载更多 -->
          <div v-if="historyHasMore" class="flex justify-center py-2">
            <button
              type="button"
              class="px-4 py-1.5 text-xs font-medium text-n-brand hover:bg-n-brand/10 rounded-md transition-colors"
              :disabled="isHistoryLoading"
              @click="loadHistories(true)"
            >
              {{ isHistoryLoading ? '加载中...' : '加载更多' }}
            </button>
          </div>
        </div>

        <!-- 空状态：假数据 + 渐变蒙层 -->
        <div v-else class="relative flex-1">
          <!-- 假表头 -->
          <div class="opacity-50 pointer-events-none">
            <div class="grid grid-cols-[1fr_64px_80px] gap-2 px-3 py-1.5 text-xs font-medium text-n-slate-10 border-b border-n-slate-3 dark:border-n-slate-700">
              <span>{{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_COL_QUERY') }}</span>
              <span class="text-right">{{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_COL_ELAPSED') }}</span>
              <span class="text-right">{{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_COL_TIME') }}</span>
            </div>
            <!-- 假数据行 -->
            <div class="space-y-0.5">
              <div
                v-for="i in 6"
                :key="i"
                class="grid grid-cols-[1fr_64px_80px] gap-2 px-3 py-2"
              >
                <span class="text-sm text-n-slate-10 truncate">
                  {{ t(`PILOT.KNOWLEDGE_BASES.RECALL_TEST.FAKE_QUERY_${i}`) }}
                </span>
                <span class="text-right text-xs text-n-slate-10 tabular-nums">
                  {{ t(`PILOT.KNOWLEDGE_BASES.RECALL_TEST.FAKE_ELAPSED_${i}`) }}
                </span>
                <span class="text-right text-xs text-n-slate-10">
                  {{ t(`PILOT.KNOWLEDGE_BASES.RECALL_TEST.FAKE_TIME_${i}`) }}
                </span>
              </div>
            </div>
          </div>
          <!-- 渐变蒙层 + 提示 -->
          <div
            class="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-transparent via-n-slate-1/90 dark:via-n-slate-800/90 to-n-slate-1 dark:to-n-slate-800"
          >
            <div class="flex flex-col items-center gap-3 px-4 text-center">
              <Icon icon="i-lucide-flask-conical" class="size-12 text-n-slate-8 mb-2" />
              <h4 class="text-xl font-medium text-n-slate-12 dark:text-n-slate-100">
                {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_EMPTY_TITLE') }}
              </h4>
              <p class="max-w-sm text-sm text-n-slate-11">
                {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.HISTORY_EMPTY_DESC') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧：结果区域 -->
    <div
      class="flex-1 flex flex-col min-w-0 rounded-lg bg-[#F2F4F7] dark:bg-n-slate-7/50 overflow-hidden h-full"
    >
      <!-- 加载状态 -->
      <div
        v-if="isSearching"
        class="flex items-center justify-center flex-1"
      >
        <Spinner />
      </div>

      <!-- 错误状态 -->
      <div
        v-else-if="errorMessage"
        class="flex flex-col items-center justify-center flex-1 gap-3 p-6"
      >
        <div
          class="flex items-center justify-center w-10 h-10 rounded-full bg-n-red-1 dark:bg-n-red-2"
        >
          <Icon icon="i-lucide-alert-circle" class="h-5 w-5 text-n-red-10" />
        </div>
        <p class="text-sm text-n-red-10 dark:text-n-red-9">
          {{ errorMessage }}
        </p>
      </div>

      <!-- 初始空状态 -->
      <div
        v-else-if="!searchResults"
        class="flex flex-col items-center justify-center flex-1 gap-3 p-6"
      >
        <Icon icon="i-lucide-flask-conical" class="size-12 text-n-slate-8 mb-2" />
        <h4 class="text-xl font-medium text-n-slate-12 dark:text-n-slate-100">
          {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.INITIAL_TITLE') }}
        </h4>
        <p class="max-w-sm text-sm text-n-slate-11 text-center">
          {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.INITIAL_DESCRIPTION') }}
        </p>
      </div>

      <!-- 搜索结果 -->
      <template v-else>
        <!-- 结果 tab + 耗时 -->
        <div
          class="flex items-center justify-between px-4 pt-3 pb-2"
        >
          <TabBar
            :tabs="resultTabs"
            :initial-active-tab="resultTabIndex"
            @tab-changed="tab => { resultTab = tab.label.includes('FAQ') ? 'faq' : 'document'; }"
          />
          <span class="text-xs text-n-slate-10">
            {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.ELAPSED', { ms: elapsedMs }) }}
          </span>
        </div>

        <div
          v-if="activeTrace"
          class="px-4 pb-3"
        >
          <div class="flex flex-wrap gap-2">
            <div
              v-for="item in processSummary"
              :key="item.label"
              class="inline-flex items-center gap-2 rounded-full bg-white dark:bg-n-slate-800 px-3 py-1.5 shadow-sm"
            >
              <div class="text-[11px] text-n-slate-10">{{ item.label }}</div>
              <div class="text-sm font-semibold text-n-slate-12 dark:text-n-slate-100">
                {{ item.value }}
              </div>
            </div>
          </div>
        </div>

        <!-- FAQ 结果列表 -->
        <div v-if="resultTab === 'faq'" class="flex-1 overflow-y-auto p-4">
          <div
            v-if="faqResults.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-12"
          >
            <Icon icon="i-lucide-message-circle-question" class="size-12 text-n-slate-8 mb-2" />
            <h4 class="text-xl font-medium text-n-slate-12 dark:text-n-slate-100">
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_EMPTY_FAQ_TITLE') }}
            </h4>
            <p class="max-w-sm text-sm text-n-slate-11 text-center">
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_EMPTY_DESC') }}
            </p>
          </div>
          <div v-else class="flex flex-col gap-3">
            <div
              v-for="(faq, idx) in faqResults"
              :key="faq.id"
              class="p-4 bg-white dark:bg-n-slate-800 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
              @click="expandedItem = { type: 'faq', data: faq }"
            >
              <!-- 顶部行：FAQ标识 + 右上角得分&排名标签 -->
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-target text-sm text-n-brand" />
                  <span class="text-xs font-mono text-n-slate-10">FAQ-{{ faq.id }}</span>
                </div>
                <div class="flex items-center gap-1.5 flex-shrink-0">
                  <span
                    v-if="getPrimaryScore(faq) != null"
                    class="relative inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium overflow-hidden border border-n-brand/20"
                    :class="scoreBadgeText"
                  >
                    <!-- 进度条背景 -->
                    <span
                      v-if="isNormalizedScore()"
                      class="absolute inset-y-0 left-0 opacity-15"
                      :class="scoreBadgeBar"
                      :style="{ width: `${Math.min(getPrimaryScore(faq) * 100, 100)}%` }"
                    />
                    <!-- 进度条竖线 -->
                    <span
                      v-if="isNormalizedScore()"
                      class="absolute top-0 bottom-0 w-0.5 bg-n-brand"
                      :style="{ left: `${Math.min(getPrimaryScore(faq) * 100, 100)}%` }"
                    />
                    <span class="relative">
                      {{ primaryScoreLabel }}
                      <span class="font-mono font-semibold">{{ formatScore(getPrimaryScore(faq)) }}</span>
                    </span>
                  </span>
                  <span
                    v-if="rerankEnabled && faq.rerank_score != null"
                    class="relative inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium overflow-hidden border border-n-brand/20"
                    :class="scoreBadgeText"
                  >
                    <span
                      class="absolute inset-y-0 left-0 opacity-15"
                      :class="scoreBadgeBar"
                      :style="{ width: `${Math.min(faq.rerank_score * 100, 100)}%` }"
                    />
                    <span
                      class="absolute top-0 bottom-0 w-0.5 bg-n-brand"
                      :style="{ left: `${Math.min(faq.rerank_score * 100, 100)}%` }"
                    />
                    <span class="relative">
                      {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_RERANK') }}
                      <span class="font-mono font-semibold">{{ formatScore(faq.rerank_score) }}</span>
                    </span>
                  </span>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md bg-n-brand/10 text-[10px] font-medium text-n-brand"
                  >
                    {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_RANK') }} #{{ idx + 1 }}
                  </span>
                </div>
              </div>
              <!-- 答案片段 -->
              <p
                class="text-sm text-n-slate-11 dark:text-n-slate-400 mt-2 line-clamp-3 hover:text-n-brand transition-colors"
              >
                {{ faq.answer }}
              </p>
              <div v-if="recallSourceForItem(faq)" class="mt-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium"
                  :class="recallSourceClass(recallSourceForItem(faq)?.tone)"
                >
                  {{ recallSourceForItem(faq)?.label }}
                </span>
              </div>
              <!-- 底部：图标 + 标准问题 + 打开按钮 -->
              <div class="flex items-center justify-between mt-3 pt-2 border-t border-n-slate-3 dark:border-n-slate-700/50">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="i-lucide-message-circle-question text-xs text-n-slate-9 flex-shrink-0" />
                  <span class="text-xs text-n-slate-11 truncate">
                    {{ faq.question }}
                  </span>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-n-brand hover:bg-n-brand/10 rounded-md transition-colors"
                    @click="expandedItem = { type: 'faq', data: faq }"
                  >
                    打开
                    <span class="i-lucide-arrow-up-right text-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 文档结果列表 -->
        <div v-if="resultTab === 'document'" class="flex-1 overflow-y-auto p-4">
          <div
            v-if="documentResults.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-12"
          >
            <Icon icon="i-lucide-file-search" class="size-12 text-n-slate-8 mb-2" />
            <h4 class="text-xl font-medium text-n-slate-12 dark:text-n-slate-100">
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_EMPTY_DOC_TITLE') }}
            </h4>
            <p class="max-w-sm text-sm text-n-slate-11 text-center">
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_EMPTY_DESC') }}
            </p>
          </div>
          <div v-else class="flex flex-col gap-3">
            <div
              v-for="(doc, idx) in documentResults"
              :key="doc.id"
              class="p-4 bg-white dark:bg-n-slate-800 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
              @click="expandedItem = { type: 'document', data: doc }"
            >
              <!-- 顶部行：Chunk标识 + 右上角得分&排名标签 -->
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-target text-sm text-n-brand" />
                  <span class="text-xs font-mono text-n-slate-10">Chunk-{{ doc.chunk_index }} · {{ doc.full_content_length }}字</span>
                </div>
                <div class="flex items-center gap-1.5 flex-shrink-0">
                  <span
                    v-if="getPrimaryScore(doc) != null"
                    class="relative inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium overflow-hidden border border-n-brand/20"
                    :class="scoreBadgeText"
                  >
                    <!-- 进度条背景 -->
                    <span
                      v-if="isNormalizedScore()"
                      class="absolute inset-y-0 left-0 opacity-15"
                      :class="scoreBadgeBar"
                      :style="{ width: `${Math.min(getPrimaryScore(doc) * 100, 100)}%` }"
                    />
                    <!-- 进度条竖线 -->
                    <span
                      v-if="isNormalizedScore()"
                      class="absolute top-0 bottom-0 w-0.5 bg-n-brand"
                      :style="{ left: `${Math.min(getPrimaryScore(doc) * 100, 100)}%` }"
                    />
                    <span class="relative">
                      {{ primaryScoreLabel }}
                      <span class="font-mono font-semibold">{{ formatScore(getPrimaryScore(doc)) }}</span>
                    </span>
                  </span>
                  <span
                    v-if="rerankEnabled && doc.rerank_score != null"
                    class="relative inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium overflow-hidden border border-n-brand/20"
                    :class="scoreBadgeText"
                  >
                    <span
                      class="absolute inset-y-0 left-0 opacity-15"
                      :class="scoreBadgeBar"
                      :style="{ width: `${Math.min(doc.rerank_score * 100, 100)}%` }"
                    />
                    <span
                      class="absolute top-0 bottom-0 w-0.5 bg-n-brand"
                      :style="{ left: `${Math.min(doc.rerank_score * 100, 100)}%` }"
                    />
                    <span class="relative">
                      {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_RERANK') }}
                      <span class="font-mono font-semibold">{{ formatScore(doc.rerank_score) }}</span>
                    </span>
                  </span>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md bg-n-brand/10 text-[10px] font-medium text-n-brand"
                  >
                    {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_RANK') }} #{{ idx + 1 }}
                  </span>
                </div>
              </div>
              <!-- 内容片段 -->
              <p
                class="text-sm text-n-slate-12 dark:text-n-slate-100 mt-2 line-clamp-4 hover:text-n-brand transition-colors"
              >
                {{ doc.content }}
              </p>
              <div v-if="recallSourceForItem(doc)" class="mt-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium"
                  :class="recallSourceClass(recallSourceForItem(doc)?.tone)"
                >
                  {{ recallSourceForItem(doc)?.label }}
                </span>
              </div>
              <!-- 底部：文件类型图标 + 文件名 + 打开按钮 -->
              <div class="flex items-center justify-between mt-3 pt-2 border-t border-n-slate-3 dark:border-n-slate-700/50">
                <div class="flex items-center gap-2 min-w-0">
                  <FileIcon
                    :file-type="doc.document_file_type || 'txt'"
                    class="h-4 w-4 flex-shrink-0"
                  />
                  <span class="text-xs text-n-slate-11 truncate">
                    {{ doc.document_name }}
                  </span>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-n-brand hover:bg-n-brand/10 rounded-md transition-colors"
                    @click="expandedItem = { type: 'document', data: doc }"
                  >
                    打开
                    <span class="i-lucide-arrow-up-right text-xs" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 查看完整内容弹窗 -->
    <Teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="expandedItem"
          class="fixed inset-0 z-40 bg-black/40"
          @click="expandedItem = null"
        />
      </transition>
      <transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="expandedItem"
          class="fixed inset-0 z-50 flex items-center justify-center p-8 pointer-events-none"
        >
          <div
            class="bg-white dark:bg-n-slate-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col pointer-events-auto"
            @click.stop
          >
            <!-- 弹窗头部：标题 + X -->
            <div class="flex items-center justify-between px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-700">
              <span class="text-sm font-medium text-n-slate-12 dark:text-n-slate-100">
                {{ expandedItem.type === 'faq' ? 'FAQ 详情' : '文档片段详情' }}
              </span>
              <button
                type="button"
                class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-2 rounded-lg transition-colors"
                @click="expandedItem = null"
              >
                <Icon icon="i-lucide-x" class="h-5 w-5" />
              </button>
            </div>
            <!-- 信息行：靶子图标 + Chunk-xx + 字符数 + 得分标签 -->
            <div class="flex items-center justify-between px-6 py-3 bg-n-slate-1 dark:bg-n-slate-800/50">
              <div class="flex items-center gap-2">
                <span class="i-lucide-target text-sm text-n-brand" />
                <span class="text-xs font-mono text-n-slate-10">
                  {{ expandedItem.type === 'faq' ? `FAQ-${expandedItem.data.id}` : `Chunk-${expandedItem.data.chunk_index}` }}
                </span>
                <template v-if="recallSourceForItem(currentTraceCandidate || expandedItem.data)">
                  <span class="text-xs text-n-slate-9">·</span>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium"
                    :class="recallSourceClass(recallSourceForItem(currentTraceCandidate || expandedItem.data)?.tone)"
                  >
                    {{ recallSourceForItem(currentTraceCandidate || expandedItem.data)?.label }}
                  </span>
                </template>
                <template v-if="expandedItem.type === 'document'">
                  <span class="text-xs text-n-slate-9">·</span>
                  <span class="text-xs text-n-slate-10">{{ expandedItem.data.full_content_length }}字</span>
                </template>
              </div>
              <div class="flex items-center gap-1.5 flex-shrink-0">
                <span
                  v-if="getPrimaryScore(expandedItem.data) != null"
                  class="relative inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium overflow-hidden border border-n-brand/20"
                  :class="scoreBadgeText"
                >
                  <span
                    v-if="isNormalizedScore()"
                    class="absolute inset-y-0 left-0 opacity-15"
                    :class="scoreBadgeBar"
                    :style="{ width: `${Math.min(getPrimaryScore(expandedItem.data) * 100, 100)}%` }"
                  />
                  <span
                    v-if="isNormalizedScore()"
                    class="absolute top-0 bottom-0 w-0.5 bg-n-brand"
                    :style="{ left: `${Math.min(getPrimaryScore(expandedItem.data) * 100, 100)}%` }"
                  />
                  <span class="relative">
                    {{ primaryScoreLabel }}
                    <span class="font-mono font-semibold">{{ formatScore(getPrimaryScore(expandedItem.data)) }}</span>
                  </span>
                </span>
                <span
                  v-if="rerankEnabled && expandedItem.data.rerank_score != null"
                  class="relative inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium overflow-hidden border border-n-brand/20"
                  :class="scoreBadgeText"
                >
                  <span
                    class="absolute inset-y-0 left-0 opacity-15"
                    :class="scoreBadgeBar"
                    :style="{ width: `${Math.min(expandedItem.data.rerank_score * 100, 100)}%` }"
                  />
                  <span
                    class="absolute top-0 bottom-0 w-0.5 bg-n-brand"
                    :style="{ left: `${Math.min(expandedItem.data.rerank_score * 100, 100)}%` }"
                  />
                  <span class="relative">
                    {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RESULT_RERANK') }}
                    <span class="font-mono font-semibold">{{ formatScore(expandedItem.data.rerank_score) }}</span>
                  </span>
                </span>
              </div>
            </div>
            <!-- 弹窗内容 -->
            <div class="flex-1 min-h-0 grid grid-cols-[minmax(0,1.4fr)_360px]">
              <div class="overflow-y-auto px-6 py-5 border-r border-n-slate-3 dark:border-n-slate-700">
                <template v-if="expandedItem.type === 'faq'">
                  <div class="text-xs text-n-slate-9 mb-1">标准问题</div>
                  <h3 class="text-base font-medium text-n-slate-12 dark:text-n-slate-100 mb-4">
                    {{ expandedItem.data.question }}
                  </h3>
                  <div class="space-y-3">
                    <div
                      v-for="(ans, aIdx) in expandedItem.data.answers"
                      :key="ans.id"
                      class="p-3 bg-n-slate-1 dark:bg-n-slate-700/50 rounded-lg"
                    >
                      <div class="text-[10px] text-n-slate-9 mb-1">答案 {{ aIdx + 1 }}</div>
                      <p class="text-sm text-n-slate-11 dark:text-n-slate-400 whitespace-pre-wrap leading-relaxed">
                        {{ ans.answer }}
                      </p>
                    </div>
                  </div>
                  <div
                    v-if="expandedItem.data.similar_questions?.length"
                    class="mt-4 pt-3 border-t border-n-slate-3 dark:border-n-slate-700"
                  >
                    <div class="text-xs text-n-slate-9 mb-2">相似问题</div>
                    <div class="flex flex-wrap gap-1.5">
                      <span
                        v-for="sq in expandedItem.data.similar_questions"
                        :key="sq.id"
                        class="inline-flex items-center px-2 py-0.5 text-xs text-n-slate-11 bg-n-slate-2 dark:bg-n-slate-700 rounded-md"
                      >
                        {{ sq.question }}
                      </span>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <p class="text-sm text-n-slate-11 dark:text-n-slate-400 whitespace-pre-wrap leading-relaxed">
                    {{ expandedItem.data.full_content }}
                  </p>
                </template>
              </div>
              <div class="overflow-y-auto px-5 py-5 bg-n-slate-1/60 dark:bg-n-slate-900/20">
                <div class="space-y-4">
                  <div>
                    <div class="text-xs font-medium text-n-slate-9 mb-2">流程过程</div>
                    <div class="space-y-2">
                      <div
                        v-for="stage in processStages"
                        :key="stage.key"
                        class="flex items-center justify-between rounded-lg bg-white dark:bg-n-slate-800 px-3 py-2"
                      >
                        <div class="min-w-0">
                          <div class="text-sm font-medium text-n-slate-12 dark:text-n-slate-100">{{ stage.title }}</div>
                          <div
                            v-if="stage.status"
                            class="text-[11px] font-medium"
                            :class="stage.status === '通过' ? 'text-n-green-10' : 'text-n-red-10'"
                          >
                            {{ stage.status }}
                          </div>
                        </div>
                        <span class="text-[11px] text-n-slate-10 text-right ml-3">{{ stage.meta }}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="text-xs font-medium text-n-slate-9 mb-2">阶段分数</div>
                    <div class="space-y-2">
                      <div
                        v-for="row in scoreRowsForItem(currentTraceCandidate || expandedItem.data)"
                        :key="row.label"
                        class="flex items-center justify-between rounded-lg bg-white dark:bg-n-slate-800 px-3 py-2"
                      >
                        <span class="text-xs text-n-slate-10">{{ row.label }}</span>
                        <span class="text-xs font-mono font-semibold text-n-slate-12 dark:text-n-slate-100">
                          {{ formatScore(row.value) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 弹窗底部：来源信息 -->
            <div class="flex items-center justify-between px-6 py-3 border-t border-n-slate-3 dark:border-n-slate-700">
              <div class="flex items-center gap-2">
                <template v-if="expandedItem.type === 'faq'">
                  <span class="i-lucide-message-circle-question text-xs text-n-slate-9" />
                  <span class="text-xs text-n-slate-10 truncate">{{ expandedItem.data.knowledge_base_name }}</span>
                </template>
                <template v-else>
                  <FileIcon :file-type="expandedItem.data.document_file_type || 'txt'" class="h-3.5 w-3.5" />
                  <span class="text-xs text-n-slate-10 truncate">{{ expandedItem.data.document_name }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- 向量检索配置抽屉 -->
    <Teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <!-- 遮罩 -->
        <div
          v-if="showConfigDrawer"
          class="fixed inset-0 z-40 bg-black/30"
          @click="showConfigDrawer = false"
        />
      </transition>
      <transition
        enter-active-class="transition-transform duration-300 ease-out"
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
      >
        <div
          v-if="showConfigDrawer"
          class="fixed right-0 top-0 bottom-0 z-50 w-[720px] bg-white dark:bg-n-slate-800 border-l border-n-weak shadow-xl flex flex-col"
        >
          <!-- 抽屉头部 -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-700">
            <h3 class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-100">
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.CONFIG_DRAWER_TITLE') }}
            </h3>
            <button
              type="button"
              class="p-1.5 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-2 rounded-lg transition-colors"
              @click="showConfigDrawer = false"
            >
              <Icon icon="i-lucide-x" class="h-5 w-5" />
            </button>
          </div>

          <!-- 抽屉内容 -->
          <div class="flex-1 overflow-y-auto px-6 py-5">
            <div class="flex flex-col gap-3">
              <div>
                <h4 class="text-sm font-medium text-n-slate-12 dark:text-n-slate-100 mb-2">
                  {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SEARCH_MODE_LABEL') }}
                </h4>
                <div class="space-y-2.5">
                  <div
                    v-for="mode in searchModes"
                    :key="mode.value"
                    class="rounded-xl border transition-all"
                    :class="searchMode === mode.value
                      ? 'border-n-brand bg-white dark:bg-n-slate-800 shadow-sm'
                      : 'border-n-slate-3 dark:border-n-slate-700 bg-white dark:bg-n-slate-800'"
                  >
                    <button
                      type="button"
                      class="w-full flex items-center justify-between px-4 py-3 text-left"
                      @click="searchMode = mode.value"
                    >
                      <div>
                        <div
                          class="text-sm font-medium"
                          :class="searchMode === mode.value ? 'text-n-brand' : 'text-n-slate-12 dark:text-n-slate-100'"
                        >
                          {{ mode.label }}
                        </div>
                        <div class="text-[11px] text-n-slate-10 mt-0.5 leading-4">
                          {{ modeConfigText[mode.value] }}
                        </div>
                      </div>
                      <span
                        class="inline-flex items-center justify-center w-5 h-5 rounded-full border text-[11px]"
                        :class="searchMode === mode.value
                          ? 'border-n-brand bg-n-brand text-white'
                          : 'border-n-slate-4 text-n-slate-9'"
                      >
                        {{ searchMode === mode.value ? '✓' : '' }}
                      </span>
                    </button>

                    <div
                      v-if="searchMode === mode.value"
                      class="px-4 pb-4 pt-3 border-t border-n-brand/10 space-y-3"
                    >
                      <div
                        v-if="mode.value !== 'hybrid'"
                        class="rounded-lg bg-white dark:bg-n-slate-900 border border-n-slate-3 dark:border-n-slate-700 px-3 py-3"
                      >
                        <div class="flex items-center justify-between gap-4">
                          <div>
                            <div class="text-sm font-medium text-n-slate-12 dark:text-n-slate-100">
                              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RERANK_LABEL') }}
                            </div>
                            <div class="text-[11px] text-n-slate-10 mt-1 leading-4">
                              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RERANK_DESC') }}
                            </div>
                          </div>
                          <button
                            type="button"
                            class="relative h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200"
                            :class="rerankEnabled ? 'bg-n-brand' : 'bg-n-slate-4'"
                            @click="rerankEnabled = !rerankEnabled"
                          >
                            <span
                              class="absolute top-1 left-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
                              :class="rerankEnabled ? 'translate-x-5' : 'translate-x-0'"
                            />
                          </button>
                        </div>
                      </div>

                      <div
                        v-if="mode.value === 'hybrid'"
                        class="rounded-lg bg-white dark:bg-n-slate-900 border border-n-slate-3 dark:border-n-slate-700 p-3"
                      >
                        <div class="text-xs font-medium text-n-slate-10 mb-2">混合方式</div>
                        <div class="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            class="rounded-xl border px-3 py-3 text-left transition-all"
                            :class="!rerankEnabled
                              ? 'border-n-brand bg-n-brand/[0.06] shadow-sm'
                              : 'border-n-slate-3 dark:border-n-slate-700 bg-white dark:bg-n-slate-950'"
                            @click="rerankEnabled = false"
                          >
                            <div class="flex items-center justify-between gap-2">
                              <div class="text-sm font-semibold" :class="!rerankEnabled ? 'text-n-brand' : 'text-n-slate-12 dark:text-n-slate-100'">权重模式</div>
                              <span
                                class="inline-flex items-center justify-center w-5 h-5 rounded-full border text-[11px] font-bold"
                                :class="!rerankEnabled ? 'border-n-brand bg-n-brand text-white' : 'border-n-slate-4 text-transparent'"
                              >
                                ✓
                              </span>
                            </div>
                            <div class="text-[11px] text-n-slate-10 mt-1 leading-4">按语义/全文加权混合分排序</div>
                          </button>
                          <button
                            type="button"
                            class="rounded-xl border px-3 py-3 text-left transition-all"
                            :class="rerankEnabled
                              ? 'border-n-brand bg-n-brand/[0.06] shadow-sm'
                              : 'border-n-slate-3 dark:border-n-slate-700 bg-white dark:bg-n-slate-950'"
                            @click="rerankEnabled = true"
                          >
                            <div class="flex items-center justify-between gap-2">
                              <div class="text-sm font-semibold" :class="rerankEnabled ? 'text-n-brand' : 'text-n-slate-12 dark:text-n-slate-100'">Rerank 模式</div>
                              <span
                                class="inline-flex items-center justify-center w-5 h-5 rounded-full border text-[11px] font-bold"
                                :class="rerankEnabled ? 'border-n-brand bg-n-brand text-white' : 'border-n-slate-4 text-transparent'"
                              >
                                ✓
                              </span>
                            </div>
                            <div class="text-[11px] text-n-slate-10 mt-1 leading-4">先统一候选，再用重排模型排序</div>
                          </button>
                        </div>
                      </div>

                      <div
                        v-if="mode.value === 'hybrid' && hybridMode === 'weighted'"
                        class="rounded-lg bg-white dark:bg-n-slate-900 border border-n-slate-3 dark:border-n-slate-700 px-3 py-3"
                      >
                        <div class="text-xs font-medium text-n-slate-10 mb-2">混合比例</div>
                        <div class="grid grid-cols-2 gap-3">
                          <div>
                            <div class="flex items-center justify-between mb-1.5">
                              <span class="text-xs font-medium text-n-slate-10">FAQ</span>
                              <span class="text-xs font-mono text-n-brand">语义 {{ faqEmbeddingWeight.toFixed(2) }} / 全文 {{ faqFullTextWeight.toFixed(2) }}</span>
                            </div>
                            <input
                              v-model.number="faqEmbeddingSlider"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-n-brand"
                            />
                            <div class="flex justify-between text-[11px] text-n-slate-10 mt-1">
                              <span>语义</span>
                              <span>全文</span>
                            </div>
                          </div>
                          <div>
                            <div class="flex items-center justify-between mb-1.5">
                              <span class="text-xs font-medium text-n-slate-10">文档</span>
                              <span class="text-xs font-mono text-n-brand">语义 {{ docEmbeddingWeight.toFixed(2) }} / 全文 {{ docFullTextWeight.toFixed(2) }}</span>
                            </div>
                            <input
                              v-model.number="docEmbeddingSlider"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-n-brand"
                            />
                            <div class="flex justify-between text-[11px] text-n-slate-10 mt-1">
                              <span>语义</span>
                              <span>全文</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="rounded-lg bg-white dark:bg-n-slate-900 border border-n-slate-3 dark:border-n-slate-700 px-3 py-3">
                        <div class="text-xs font-medium text-n-slate-10 mb-2">召回参数</div>
                        <div :class="mode.value === 'hybrid' ? 'grid grid-cols-2 gap-3' : 'space-y-3'">
                          <div
                            v-if="mode.value === 'semantic' || mode.value === 'hybrid'"
                            class="grid grid-cols-[120px_1fr] items-center gap-3"
                          >
                            <div>
                              <div class="text-xs font-medium text-n-slate-10">Vector Top K</div>
                              <div class="text-sm font-mono font-semibold text-n-slate-12 dark:text-n-slate-100 mt-1">{{ vectorTopK }}</div>
                            </div>
                            <input
                              v-model.number="vectorTopK"
                              type="range"
                              min="5"
                              max="50"
                              step="1"
                              class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-n-brand"
                            />
                          </div>
                          <div
                            v-if="mode.value === 'full_text' || mode.value === 'hybrid'"
                            class="grid grid-cols-[120px_1fr] items-center gap-3"
                          >
                            <div>
                              <div class="text-xs font-medium text-n-slate-10">Full-text Top K</div>
                              <div class="text-sm font-mono font-semibold text-n-slate-12 dark:text-n-slate-100 mt-1">{{ fullTextTopK }}</div>
                            </div>
                            <input
                              v-model.number="fullTextTopK"
                              type="range"
                              min="5"
                              max="50"
                              step="1"
                              class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-n-brand"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="rounded-lg bg-white dark:bg-n-slate-900 border border-n-slate-3 dark:border-n-slate-700 px-3 py-3">
                        <div class="text-xs font-medium text-n-slate-10 mb-2">Score</div>
                        <div class="grid grid-cols-2 gap-3">
                          <div class="space-y-1.5">
                            <div class="flex items-center justify-between">
                              <span class="text-xs font-medium text-n-slate-10">FAQ 阈值</span>
                              <span class="text-sm font-mono font-semibold text-n-slate-12 dark:text-n-slate-100">{{ faqThreshold.toFixed(2) }}</span>
                            </div>
                            <div class="text-[11px] text-n-slate-10">推荐 {{ recommendedScoreRange.faq }}</div>
                            <input
                              v-model.number="faqThreshold"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-n-brand"
                            />
                          </div>
                          <div class="space-y-1.5">
                            <div class="flex items-center justify-between">
                              <span class="text-xs font-medium text-n-slate-10">文档阈值</span>
                              <span class="text-sm font-mono font-semibold text-n-slate-12 dark:text-n-slate-100">{{ docThreshold.toFixed(2) }}</span>
                            </div>
                            <div class="text-[11px] text-n-slate-10">推荐 {{ recommendedScoreRange.document }}</div>
                            <input
                              v-model.number="docThreshold"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-n-brand"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="rounded-lg bg-white dark:bg-n-slate-900 border border-n-slate-3 dark:border-n-slate-700 px-3 py-3">
                        <div class="text-xs font-medium text-n-slate-10 mb-2">Top N</div>
                        <div class="grid grid-cols-[120px_1fr] items-center gap-3">
                          <div>
                            <div class="text-xs font-medium text-n-slate-10">最终返回数量</div>
                            <div class="text-sm font-mono font-semibold text-n-slate-12 dark:text-n-slate-100 mt-1">{{ topN }}</div>
                          </div>
                          <input
                            v-model.number="topN"
                            type="range"
                            min="1"
                            max="20"
                            step="1"
                            class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-n-brand"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 抽屉底部 -->
          <div class="flex gap-3 px-6 py-4 border-t border-n-weak">
            <Button
              :label="t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.APPLY_TEST')"
              icon="i-lucide-flask-conical"
              class="flex-1"
              @click="showConfigDrawer = false"
            />
            <button
              type="button"
              class="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-slate-2 dark:bg-n-slate-700 rounded-lg hover:bg-n-slate-3 dark:hover:bg-n-slate-600 transition-colors"
              @click="handleSaveConfig"
            >
              <span class="i-lucide-save text-sm" />
              {{ t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SAVE_AND_APPLY') }}
            </button>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- 保存配置确认弹窗 -->
    <Dialog
      ref="confirmSaveDialogRef"
      type="alert"
      :title="t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SAVE_CONFIG_CONFIRM_TITLE')"
      :description="t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SAVE_CONFIG_CONFIRM')"
      :confirm-button-label="t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.SAVE_AND_APPLY')"
      :is-loading="isSavingConfig"
      @confirm="handleSaveConfigConfirm"
    />

    <!-- 帮助说明弹窗 -->
    <Dialog
      ref="helpDialogRef"
      type="edit"
      :title="helpDialogType === 'recall' ? t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RECALL_HELP_TITLE') : t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RERANK_HELP_TITLE')"
      :show-confirm-button="false"
      :cancel-button-label="t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.CLOSE')"
      width="lg"
    >
      <p class="text-sm text-n-slate-11 whitespace-pre-wrap leading-relaxed">
        {{ helpDialogType === 'recall' ? t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RECALL_HELP_CONTENT') : t('PILOT.KNOWLEDGE_BASES.RECALL_TEST.RERANK_HELP_CONTENT') }}
      </p>
    </Dialog>
  </div>
</template>
